/**
 * Per-user settings (brief §13, `/api/me/settings`).
 *
 * Stored as one `jsonb` column rather than a table of columns, and normalised on the way in
 * and on the way out. The reason is that this is the part of the schema that grows: every
 * slice from here adds a preference, and a migration per checkbox is a bad trade for a
 * document that is read exactly once per session.
 *
 * Normalising on read is what makes that safe. A row written by an older build is missing
 * keys a newer one expects, and the answer is the default rather than `undefined` leaking
 * into a comparison.
 */

import type { AlertSettings } from './geofence.ts';
import { DEFAULT_ALERT_SETTINGS, normaliseAlertSettings } from './geofence.ts';
import type { Cell } from './h3.ts';
import { REGION_RESOLUTION } from './h3.ts';

export type UserSettings = {
  alerts: AlertSettings;
  /** What a new report defaults to: 0 private, 1 friends, 2 everyone (brief §7). */
  defaultVisibility: 0 | 1 | 2;
  /**
   * A res-5 cell the user volunteers as their home region, for the "somebody reported near
   * you" push (brief §16).
   *
   * Res 5 is ≈ 250 km², which is a conurbation and not a neighbourhood. It is opt-in, it is
   * the coarsest cell the app knows, and it is the only location the *server* ever holds for
   * a person — which is why it is stored as a cell index and not as a place name that could
   * be narrower.
   */
  homeCell5: Cell | null;
  /** Reminders for the pass-by swipe stack (brief §9a.1). */
  swipeReminders: boolean;
};

export const DEFAULT_SETTINGS: UserSettings = {
  alerts: DEFAULT_ALERT_SETTINGS,
  defaultVisibility: 2,
  homeCell5: null,
  swipeReminders: true,
};

const CELL_SHAPE = /^[0-9a-f]{15}$/;

/** Res-5 and nothing finer. A res-9 cell here would be a home address in a `jsonb` column. */
export function isRegionCell(value: unknown, resolutionOf: (cell: Cell) => number): value is Cell {
  return (
    typeof value === 'string' && CELL_SHAPE.test(value) && resolutionOf(value) === REGION_RESOLUTION
  );
}

export function normaliseSettings(
  input: unknown,
  resolutionOf: (cell: Cell) => number,
): UserSettings {
  const raw = (input ?? {}) as Record<string, unknown>;
  const visibility = raw.defaultVisibility;
  return {
    alerts: normaliseAlertSettings(raw.alerts),
    defaultVisibility:
      visibility === 0 || visibility === 1 || visibility === 2
        ? visibility
        : DEFAULT_SETTINGS.defaultVisibility,
    homeCell5: isRegionCell(raw.homeCell5, resolutionOf) ? raw.homeCell5 : null,
    swipeReminders:
      typeof raw.swipeReminders === 'boolean'
        ? raw.swipeReminders
        : DEFAULT_SETTINGS.swipeReminders,
  };
}
